import React, {useEffect, useState} from 'react';
import {Button, Col, Form, Input, Modal, Row, Spin, Table, Space} from 'antd';
import {IngredientDataType} from '../models/column';
import {selectorItemsApi} from '../apiCalls/selectorItemsApi';
import PageTitle from '../components/PageTitle';
import PageLoading from '../components/PageLoading';
import {ColumnsType} from 'antd/es/table';
import {EditOutlined, PlusCircleOutlined, PlusOutlined, SearchOutlined} from '@ant-design/icons';
import type {TableColumnType} from 'antd';
import type {FilterDropdownProps} from 'antd/es/table/interface';
import TableFiltersBar, {applyFiltersToColumns, useTableFilters} from '../components/TableFilters';

const Providers: React.FC = () => {
  const [dataSource, setDataSource] = useState<IngredientDataType[]>([]);
  const [column, setColumn] = useState<ColumnsType<IngredientDataType>>([]);

  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = React.useRef<any>(null);
  const {filters, handleTableChange, clearAllFilters, removeFilterValue} = useTableFilters<IngredientDataType>();

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm'], dataIndex: string) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
      <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}
          >
            Buscar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({closeDropdown: false});
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
              close();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />,
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await selectorItemsApi.getItemList('/lists/supplier');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const allKeys = [...new Set(data.flatMap(Object.keys))];

      setColumn([
        ...allKeys.map((item) => {
          return {
            title: item,
            key: item,
            dataIndex: item,
            ...(item === 'name' || item === 'code' ? getColumnSearchProps(item) : {}),
          } as any;
        }),
        {
          title: '',
          key: 'operation',
          fixed: 'right',
          width: 100,
          align: 'center',
          render: (text, record) => (
            <div>
              <EditOutlined onClick={() => handleEdit(record)} />
            </div>
          ),
        },
      ]);

      const source = data.map((item: any, index: number) => ({
        ...item,
        id: item?.id,
      }));

      setDataSource(source);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setSelectedItem(record);
    setIsModalVisible(true);
    form.setFieldsValue({name: record.name});
  };

  const handleDelete = async (record: any) => {
    await selectorItemsApi.deleteItemList('/lists/supplier', record.id);
    fetchItems();
  };

  if (loading) {
    return <PageLoading />;
  }

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setConfirmLoading(true);

        await selectorItemsApi.setItemList('/lists/supplier', values.name);
        setConfirmLoading(false);
        setIsModalVisible(false);
        form.resetFields();
        fetchItems();
      })
      .catch(() => {
        // error handled silently
      });
  };

  const handleEditConfirm = async () => {
    const values = await form.validateFields();
    // TODO
    // await selectorItemsApi.editItemList('/lists/supplier', selectedItem?.id, values.name);
    setSelectedItem(null);
    setIsModalVisible(false);
    form.resetFields();
    fetchItems();
  };
  const tableColumns = applyFiltersToColumns(column, filters);

  return (
    <div>
      <Modal
        confirmLoading={confirmLoading}
        title={selectedItem ? `Edit supplier` : `Add new supplier`}
        onOk={selectedItem ? handleEditConfirm : handleOk}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Form.Item label="Supplier name" name="name" rules={[{ required: true, message: 'Please input name' }]}>
                <Input placeholder="Enter supplier name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

      </Modal>

      <div style={{
        marginLeft: 50,
        marginRight: 50,
        marginTop: 30,
        flexDirection: 'row',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h2 style={{marginBottom: 16}}>Suppliers</h2>
        <Button type="primary" onClick={() => setIsModalVisible(true)}
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          Add Supplier
          <PlusOutlined style={{marginLeft: 20, fontSize: 16}}/>
        </Button>
      </div>

      <div style={{marginLeft: 50, marginRight: 50, marginTop: 20}}>
        <TableFiltersBar
          columns={tableColumns}
          filters={filters}
          onClearAll={clearAllFilters}
          onRemove={removeFilterValue}
        />
        <Table
          columns={tableColumns}
          pagination={false}
          dataSource={dataSource}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default Providers;
